import React from 'react';
import { Button } from './ui/button';
import { User, LogOut, Settings, Phone, Menu, Shield } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import NotificationsBell from './NotificationsBell';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Badge } from './ui/badge';

const Header = ({ onMenuToggle, isMobile }) => {
  const { user, company, logout } = useAuth();

  const companyName = company?.name || 'SecurESSI-AI';

  return (
    <header className="bg-white border-b border-slate-200 px-4 lg:px-6 py-3 flex-shrink-0" data-testid="app-header">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          {/* Hamburger menu - mobile */}
          {isMobile && (
            <button
              onClick={onMenuToggle}
              className="p-2 rounded-lg hover:bg-slate-100 text-slate-600 transition-colors"
              data-testid="mobile-menu-toggle"
            >
              <Menu className="w-5 h-5" />
            </button>
          )}

          <div className="flex items-center gap-2 min-w-0">
            <Shield className="w-5 h-5 text-cyan-600 flex-shrink-0 hidden sm:block" />
            <h1 className="text-base lg:text-lg font-semibold text-slate-800 truncate" data-testid="header-company-name">
              {companyName}
            </h1>
          </div>

          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 flex-shrink-0 hidden sm:flex">
            En ligne
          </Badge>
        </div>

        <div className="flex items-center gap-2 lg:gap-3">
          {/* Notifications */}
          <NotificationsBell />

          {/* User menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="flex items-center gap-2 max-w-[180px]" data-testid="user-menu-trigger">
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-xs font-bold">
                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </span>
                </div>
                <span className="hidden md:block text-sm text-slate-700 truncate">{user?.name}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <div className="px-3 py-2">
                <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                <p className="text-xs text-slate-500">{user?.email}</p>
                <p className="text-xs text-cyan-600 font-medium mt-1">{companyName}</p>
                <Badge className="mt-1 text-[10px] bg-cyan-100 text-cyan-700 hover:bg-cyan-100">
                  {user?.role === 'admin' ? 'Administrateur' : user?.role || 'Agent'}
                </Badge>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem>
                <Settings className="w-4 h-4 mr-2" />
                Parametres
              </DropdownMenuItem>
              {user?.phone && (
                <DropdownMenuItem>
                  <Phone className="w-4 h-4 mr-2" />
                  {user.phone}
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-red-600" data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Se deconnecter
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};

export default Header;
